import React from "react";
import {
  Box,
  Stack,
  Typography,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";

interface CircularProgressIndicatorProps {
  value: number;
  label: string;
  icon: React.ReactNode;
  color?: string;
  unit?: string;
  size?: number;
  max?: number;
  showExtraTrack?: boolean;
  extraTrackValue?: number;
  extraTrackColor?: string;
}

export const CircularProgressIndicator: React.FC<
  CircularProgressIndicatorProps
> = ({
  value,
  label,
  icon,
  color,
  unit,
  size,
  max = 100,
  showExtraTrack = false,
  extraTrackValue,
  extraTrackColor,
}) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only("xs"));
  const isSm = useMediaQuery(theme.breakpoints.down("md"));
  const isMd = useMediaQuery(theme.breakpoints.down("lg"));
  const isLg = useMediaQuery(theme.breakpoints.down("xl"));
  const isXl = useMediaQuery(theme.breakpoints.between("lg", "xl"));

  const getResponsiveSize = () => {
    if (size !== undefined) return size;
    if (isXs) return 60;
    if (isSm) return 70;
    if (isMd) return 70;
    if (isLg) return 85;
    if (isXl) return 120;
    return 120;
  };

  const responsiveSize = getResponsiveSize();

  const progressColor = color || theme.palette.primary.main;
  const normalizedValue = max > 0 ? (value / max) * 100 : value;
  const normalizedExtraValue =
    extraTrackValue && max > 0
      ? (extraTrackValue / max) * 100
      : extraTrackValue || 0;

  const displayUnit = unit !== undefined ? unit : max === 100 ? "%" : undefined;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
      }}
    >
      <Box sx={{ position: "relative", display: "inline-flex" }}>
        <CircularProgress
          variant="determinate"
          value={100}
          size={responsiveSize}
          thickness={4}
          sx={{
            color: theme.palette.action.disabledBackground,
            position: "absolute",
            "& .MuiCircularProgress-circle": {
              strokeLinecap: "round",
            },
          }}
        />
        {showExtraTrack && extraTrackValue && (
          <CircularProgress
            variant="determinate"
            value={normalizedExtraValue}
            size={responsiveSize}
            thickness={4}
            sx={{
              color: extraTrackColor || theme.palette.action.hover,
              position: "absolute",
              opacity: 0.5,
              "& .MuiCircularProgress-circle": {
                strokeLinecap: "round",
              },
            }}
          />
        )}
        <CircularProgress
          variant="determinate"
          value={normalizedValue}
          size={responsiveSize}
          thickness={4}
          sx={{
            color: progressColor,
            position: "relative",
            "& .MuiCircularProgress-circle": {
              strokeLinecap: "round",
            },
          }}
        />
        <Box
          sx={{
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            position: "absolute",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            variant={isXs ? "h6" : "h5"}
            component="div"
            fontWeight={700}
            sx={{
              fontSize: {
                xs: "1rem",
                sm: "1.25rem",
                md: "1.5rem",
                lg: "1.5rem",
              },
            }}
          >
            {value}
            {displayUnit}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 0.5,
          mt: 0.75,
        }}
      >
        <Box
          sx={{
            color: "text.secondary",
            display: "flex",
            alignItems: "center",
          }}
        >
          {icon}
        </Box>
        <Typography
          variant="caption"
          color="text.primary"
          sx={{ textTransform: "uppercase" }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  );
};
